import axios from 'axios';

// ---------------------------------------------------------------------------
// Creates a playbook node straight from an AI draft — the "Generate with AI"
// quick-create path and the emailed resume-review page both land here, so the
// payload shape (mirrors AddEditNode.submit) lives in exactly one place.
// ---------------------------------------------------------------------------

export type AINodeType = 'process' | 'checklist' | 'sheet' | 'timeline';

export const NODE_TYPE_CODES: { [k in AINodeType]: string } = {
    process: 'PROCESS', checklist: 'CHECKLIST', sheet: 'SHEET', timeline: 'TIMELINE'
};
export const EDIT_ROUTES: { [k in AINodeType]: string } = {
    process: '/edit-process/', checklist: '/edit-checklist/', sheet: '/edit-sheet/', timeline: '/edit-timeline/'
};

// The user-adjustable details collected in the AI modal's review step
export interface DraftMeta {
    name: string;
    slug: string;
    processOwnerIDs: number[]; // tblUser IDs, saved as ProcessOwnersJSON
    locations: string[];
    departments: string[];
    types: string[];
    folderSlugs: string[]; // folders (or 'home') to file it under, one tblNextNode link each; at least one
    videoTitle?: string; // title for a kept recording's Video node ("… (Built by the Forge)" is added by the server)
}

export function slugify(name: string): string {
    // Same rule as AddEditNode.setSlug, plus collapsing the double underscores
    // an AI title like "Battery Maintenance — Weekly" would otherwise produce
    return (name || '').toLowerCase().replace(/[^a-zA-Z0-9_ ]/g, '').replace(/ /g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
}

// Builds the details defaults from an AI draft + the company lists on the
// session user: suggested departments/types where they match, all locations,
// the creator as the process owner, and the folder it was started from (if any).
export function metaFromDraft(draft: any, user: any, fromSlug?: string | null): DraftMeta {
    const companyDepartments: string[] = (user?.departments || []).slice();
    const companyTypes: string[] = (user?.nodeTypes || []).slice();
    const name = String(draft?.name || '').trim();
    return {
        name,
        slug: slugify(name),
        processOwnerIDs: user?.userID ? [user.userID] : [],
        locations: (user?.locations || []).slice(),
        departments: (draft?.suggestedDepartments || []).filter((d: string) => companyDepartments.includes(d)),
        types: (draft?.suggestedTypes || []).filter((t: string) => companyTypes.includes(t)),
        folderSlugs: fromSlug ? [fromSlug] : []
    };
}

// Content columns for each node type, from the client-shape draft the modal emits
function contentFields(nodeType: AINodeType, draft: any): any {
    if (nodeType === 'checklist') return { ChecklistJSON: JSON.stringify({ items: draft.items || [] }) };
    if (nodeType === 'sheet') {
        // The seats model (section and seat ids assigned server-side)
        const sheet = draft.sheet || { v: 2, sections: [], seats: [] };
        return { SheetJSON: JSON.stringify(sheet), SheetColsJSON: JSON.stringify(sheet.seats.map((seat: any) => ({ id: seat.id, accountabilities: seat.accountabilities || [] }))) };
    }
    if (nodeType === 'timeline') return { TimelineJSON: JSON.stringify({ items: draft.items || [] }) };
    return {
        ProcessJSON: JSON.stringify({
            items: (draft.items || []).map((item: any) => ({ header: item.header, body: item.body, image: item.image || '' }))
        })
    };
}

export interface CreateNodeArgs {
    apiUrl: string;
    nodeType: AINodeType;
    draft: any;
    meta: DraftMeta;
    fromSlug?: string | null; // link the new node under a playbook tree node (used when meta has no folderSlugs)
    jobId?: number | string | null; // background capture job to mark consumed
}

// Returns the created slug. Throws on failure (message is user-facing);
// slug collisions retry with a numeric suffix like the resume-review page did.
export async function createNodeFromDraft(args: CreateNodeArgs): Promise<string> {
    const { apiUrl, nodeType, draft, meta } = args;
    const name = (meta.name || draft.name || '').trim();
    if (!name) throw new Error('Give the ' + nodeType + ' a name first');
    if (!meta.locations.length) throw new Error('Include at least one location');
    if (!meta.departments.length) throw new Error('Include at least one department');
    if (meta.folderSlugs && !meta.folderSlugs.length) throw new Error('Pick at least one folder');
    const baseSlug = slugify(meta.slug || name) || 'ai_' + nodeType;
    const folders: string[] = Array.from(new Set(meta.folderSlugs ? meta.folderSlugs : args.fromSlug ? [args.fromSlug] : []));
    const payload: any = {
        isNew: true,
        metaUpdate: true,
        aiGenerated: true,
        Type: NODE_TYPE_CODES[nodeType],
        Name: name,
        Link: null,
        ProcessOwnersJSON: nodeType === 'checklist' ? '[]' : JSON.stringify(meta.processOwnerIDs || []),
        fromSlug: folders[0] || null,
        LocationsJSON: JSON.stringify(meta.locations),
        DepartmentsJSON: JSON.stringify(meta.departments),
        TypesJSON: meta.types.length ? JSON.stringify(meta.types) : null,
        ...contentFields(nodeType, draft)
    };
    let createdSlug = '';
    for (let attempt = 0; attempt < 6 && !createdSlug; attempt++) {
        const slug = attempt === 0 ? baseSlug : `${baseSlug}_${attempt + 1}`;
        try {
            await axios.post(`${apiUrl}/api/playbook/node`, { ...payload, Slug: slug });
            createdSlug = slug;
        } catch (e: any) {
            const msg = String(e?.response?.data || '');
            if (e?.response?.status === 403) throw Object.assign(new Error('Your session has expired — please sign in again'), { status: 403 });
            if (!msg.includes('slug is already taken')) throw new Error(msg || 'There was an error creating the ' + nodeType);
            // collision → retry with a suffix
        }
    }
    if (!createdSlug) throw new Error('A ' + nodeType + ' with this name already exists — change the name and try again.');
    // The first folder is linked by the create call; the rest the same way "Add to folder" does
    const missed: string[] = [];
    for (const folder of folders.slice(1)) {
        await axios.post(`${apiUrl}/api/playbook/nodelink`, { fromSlug: folder, toSlug: createdSlug }).catch(() => missed.push(folder));
    }
    if (missed.length) alert(`It was created, but couldn't be added to ${missed.length == 1 ? 'one of the folders' : missed.length + ' of the folders'}. Use "Add to folder" on the Folders page to finish filing it.`);
    if (args.jobId) {
        // Mark the background capture job consumed so its email link can't double-create; a kept
        // recording becomes a Video node beside the new node (any type since 27 Oct 2026)
        axios.post(`${apiUrl}/api/ai/video-jobs/${args.jobId}/consume`, { slug: createdSlug, videoTitle: meta.videoTitle || null }).catch(() => { /* non-fatal */ });
    }
    return createdSlug;
}
